import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  useTheme,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  useMediaQuery,
  Fade,
  Zoom,
  Slide
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import {
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  Timeline as TimelineIcon,
  Psychology as PsychologyIcon,
  MenuBook as MenuBookIcon,
  Gavel as GavelIcon,
  Notifications as NotificationsIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  Announcement as AnnouncementIcon,
  EmojiEvents as EmojiEventsIcon,
  Groups as GroupsIcon,
  Forum as ForumIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Lightbulb as LightbulbIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { communityBoards } from '../data/communityBoards';
import Footer from './Footer';
import { useApp } from '../context/AppContext';
import { getSuccessStories } from '../lib/community';

const LandingPage = ({ onLoginClick, onSignupClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useApp();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeSection, setActiveSection] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  // 합격자 스토리 데이터 (최신 2개를 서버에서 가져옴)
  const [successStories, setSuccessStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  useEffect(() => {
    async function fetchStories() {
      setLoadingStories(true);
      try {
        const { stories } = await getSuccessStories({ page: 1, limit: 2 });
        setSuccessStories(stories || []);
      } catch (e) {
        setSuccessStories([]);
      } finally {
        setLoadingStories(false);
      }
    }
    fetchStories();
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.2 }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  const features = [
    {
      icon: <SchoolIcon fontSize="large" color="primary" />,
      title: '효율적인 학습',
      description: '개인화된 학습 경험을 통해 법학 용어를 효과적으로 학습하세요.'
    },
    {
      icon: <TimelineIcon fontSize="large" color="primary" />,
      title: '학습 진도 관리',
      description: '나만의 학습 진도를 추적하고 관리할 수 있습니다.'
    },
    {
      icon: <PsychologyIcon fontSize="large" color="primary" />,
      title: '맞춤형 오답노트',
      description: '틀린 문제를 자동으로 기록하고 복습할 수 있습니다.'
    }
  ];

  // Bar Exam 공지사항 데이터
  const barExamNews = [
    {
      title: "2024년 2월 뉴욕 Bar Exam 일정 발표",
      date: "2023-12-15",
      content: "2024년 2월 뉴욕 Bar Exam이 2월 27-28일에 실시됩니다.",
      link: "#"
    },
    {
      title: "2023년 7월 시험 합격률 발표",
      date: "2023-11-20",
      content: "뉴욕주 Bar Exam 합격률 65%, 한국 응시자 합격률 72%",
      link: "#"
    },
    {
      title: "UBE 도입 주 확대",
      date: "2023-11-10",
      content: "2024년부터 추가로 2개 주가 UBE를 도입할 예정입니다.",
      link: "#"
    }
  ];

  // 데모 퀴즈 컴포넌트
  const DemoQuiz = () => {
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [isCorrect, setIsCorrect] = useState(null);
    const [loading, setLoading] = useState(false);

    const quizzes = [
      {
        question: "What is 'Consideration' in Contract Law?",
        type: "definition",
        placeholder: "Write the definition in English",
        correctAnswer: ["bargained", "exchange", "value", "benefit", "detriment"],
        explanation: "Consideration is something of value that is bargained for and exchanged between parties to a contract. It can be a benefit to the promisor or a detriment to the promisee.",
        hint: "Think about what each party must give or receive in a contract..."
      },
      {
        question: "In Criminal Law, what is the legal term for 'a person's awareness that their conduct is criminal or wrongful'?",
        type: "term",
        placeholder: "Enter the legal term in English",
        correctAnswer: ["mens rea"],
        explanation: "Mens Rea (guilty mind) is the mental element of a crime, referring to the defendant's awareness of the wrongfulness of their actions.",
        hint: "This Latin term literally means 'guilty mind'"
      },
      {
        question: "What are the four elements of Negligence in Tort Law?",
        type: "list",
        placeholder: "List all four elements, separated by commas",
        correctAnswer: ["duty", "breach", "causation", "damages"],
        explanation: "The four elements are: (1) Duty of care, (2) Breach of duty, (3) Causation, and (4) Damages/Harm.",
        hint: "Remember the sequence: First comes responsibility, then failing that responsibility..."
      }
    ];

    const currentQuiz = quizzes[currentQuizIndex];

    const handleSubmit = (e) => {
      e.preventDefault();
      setLoading(true);
      
      setTimeout(() => {
        let isAnswerCorrect = false;
        const normalizedAnswer = answer.toLowerCase().trim();

        if (currentQuiz.type === "definition") {
          isAnswerCorrect = currentQuiz.correctAnswer.every(keyword => 
            normalizedAnswer.includes(keyword.toLowerCase())
          );
        } else if (currentQuiz.type === "term") {
          isAnswerCorrect = currentQuiz.correctAnswer.some(term => 
            normalizedAnswer === term.toLowerCase()
          );
        } else if (currentQuiz.type === "list") {
          const answerElements = normalizedAnswer.split(',').map(el => el.trim());
          isAnswerCorrect = currentQuiz.correctAnswer.every(element =>
            answerElements.some(answer => answer === element.toLowerCase())
          );
        }

        setIsCorrect(isAnswerCorrect);
        setLoading(false);
      }, 1000);
    };

    const handleNext = () => {
      setCurrentQuizIndex((prev) => (prev + 1) % quizzes.length);
      setAnswer('');
      setIsCorrect(null);
    };

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Demo Quiz
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentQuizIndex + 1} / {quizzes.length}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Q. {currentQuiz.question}
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={currentQuiz.type === "definition" ? 3 : 1}
              variant="outlined"
              placeholder={currentQuiz.placeholder}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={loading || isCorrect !== null}
              sx={{ mb: 2 }}
            />
            
            {currentQuiz.hint && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                💡 Hint: {currentQuiz.hint}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || isCorrect !== null}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Answer'}
            </Button>
          </form>

          {isCorrect !== null && (
            <Box sx={{ mt: 2 }}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: isCorrect ? 'success.light' : 'error.light',
                  color: isCorrect ? 'success.dark' : 'error.dark',
                  borderRadius: 1,
                  mb: 2
                }}
              >
                <Typography variant="body1" gutterBottom>
                  {isCorrect ? 'Correct!' : 'Not quite right.'}
                </Typography>
                <Typography variant="body2">
                  {currentQuiz.explanation}
                </Typography>
              </Paper>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleNext}
              >
                Next Question
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // 버튼 클릭 핸들러
  const handleLoginClick = (e) => {
    e.preventDefault();
    console.log('LandingPage: Login button clicked');
    if (typeof onLoginClick === 'function') {
      onLoginClick();
    }
  };

  const handleSignupClick = (e) => {
    e.preventDefault();
    console.log('LandingPage: Signup button clicked');
    if (typeof onSignupClick === 'function') {
      onSignupClick();
    }
  };

  const handlePricingClick = (e) => {
    e.preventDefault();
    navigate('/pricing');
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Hero Section - Parallax Effect */}
      <Box
        id="hero-section"
        className="animate-section"
        sx={{
          position: 'relative',
          minHeight: { xs: '400px', md: '500px' },
          maxHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #303f9f 100%)',
          overflow: 'hidden',
          py: { xs: 3, md: 4 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
            opacity: 0.6,
            animation: 'float 20s linear infinite',
          },
          '@keyframes float': {
            '0%': { transform: 'translateY(0) translateX(0)' },
            '50%': { transform: 'translateY(-20px) translateX(20px)' },
            '100%': { transform: 'translateY(0) translateX(0)' },
          }
        }}
      >
        <Container maxWidth="lg">
          <Fade in={isVisible['hero-section']} timeout={1000}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 900,
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1.5,
                    letterSpacing: '0.02em',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.15)',
                    lineHeight: 1.2,
                    whiteSpace: 'pre-line'
                  }}
                >
                  Master{'\n'}
                  Black Letter Law & MPT/MEE
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' },
                    color: 'rgba(255, 255, 255, 0.9)',
                    mb: { xs: 1.5, md: 2 },
                    letterSpacing: '0.01em',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                  }}
                >
                  Pass the Bar Exam with Confidence
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: { xs: 1.5, md: 2 },
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                    background: 'linear-gradient(135deg, #E3F2FD 0%, #90CAF9 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontStyle: 'normal',
                    letterSpacing: '0.02em',
                    fontFamily: 'Pretendard, sans-serif',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.15)',
                    whiteSpace: 'pre-line'
                  }}
                >
                  미국 변호사 시험,{'\n'}
                  BLL Pro와 MPT/MEE 실전테스트로 완벽 대비
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: { xs: 2, md: 3 },
                    fontFamily: 'Pretendard, sans-serif',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    color: 'rgba(255, 255, 255, 0.75)'
                  }}
                >
                  법률 용어 학습부터 실전 문제 연습까지 한번에
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: { xs: 1, sm: 1.5 },
                  transform: { xs: 'scale(0.85)', md: 'scale(0.9)' }
                }}>
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '10px',
                      height: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 15px 30px rgba(0,0,0,0.3)',
                      background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #303f9f 100%)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url(/images/bll-pro.png)',
                        backgroundSize: '60%',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: 0.2,
                        zIndex: 1,
                      },
                    }}
                  >
                    {/* 법률책 디자인 */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '80%',
                        height: '70%',
                        background: 'linear-gradient(to right, #f5f5f5, #fff)',
                        borderRadius: '5px',
                        boxShadow: '10px 10px 20px rgba(0,0,0,0.4)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '25px',
                          background: 'linear-gradient(to bottom, #d32f2f, #b71c1c)',
                          borderRadius: '5px 0 0 5px',
                        },
                      }}
                    >
                      <GavelIcon 
                        sx={{ 
                          position: 'absolute',
                          top: '10%',
                          right: '10%',
                          fontSize: '40px',
                          color: theme.palette.primary.main,
                          opacity: 0.8,
                          transform: 'rotate(15deg)',
                        }}
                      />
                      
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 900,
                          color: theme.palette.primary.main,
                          mb: 1,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          textShadow: '1px 1px 1px rgba(0,0,0,0.2)',
                          fontFamily: '"Roboto Slab", serif',
                        }}
                      >
                        BLL Pro
                      </Typography>
                      
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                          textAlign: 'center',
                          maxWidth: '80%',
                          fontStyle: 'italic'
                        }}
                      >
                        Master the Black Letter Law
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 2,
                          mt: 3
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <MenuBookIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            fontSize: '1rem'
                          }}>
                            법률 용어 완벽 학습
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <CheckIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            fontSize: '1rem'
                          }}>
                            체계적인 학습 관리
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <TimelineIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            fontSize: '1rem'
                          }}>
                            맞춤형 학습 분석
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Features Section - Horizontal Scroll on Mobile */}
      <Box
        id="features-section"
        className="animate-section"
        sx={{
          py: 8,
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              transform: isVisible['features-section'] ? 'translateY(0)' : 'translateY(50px)',
              opacity: isVisible['features-section'] ? 1 : 0,
              transition: 'all 0.6s ease-out'
            }}
          >
            주요 서비스
          </Typography>
          <Grid container spacing={6}>
            {/* BLL Pro 서비스 */}
            <Grid item xs={12} md={6}>
              <Zoom in={isVisible['features-section']} style={{ transitionDelay: '100ms' }}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    transform: 'translateY(0)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 6,
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Typography variant="h4" gutterBottom fontWeight={700} color="primary">
                        BLL Pro
                      </Typography>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                        Black Letter Law 완벽 학습
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <MenuBookIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="법률 용어 체계적 학습" 
                          secondary="핵심 법률 용어를 효율적으로 학습"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="맞춤형 학습 관리" 
                          secondary="개인별 학습 진도와 성취도 추적"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <TimelineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="상세한 학습 분석" 
                          secondary="취약점 분석과 맞춤형 복습"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
            
            {/* MPT/MEE 실전테스트 서비스 */}
            <Grid item xs={12} md={6}>
              <Zoom in={isVisible['features-section']} style={{ transitionDelay: '200ms' }}>
                    <Card
                      sx={{
                        height: '100%',
                        bgcolor: theme.palette.grey[200],
                        color: theme.palette.text.primary,
                        transform: 'translateY(0)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-10px)',
                          boxShadow: 6,
                        }
                      }}
                    >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Typography variant="h4" gutterBottom fontWeight={700} color="secondary">
                        MPT/MEE 실전테스트
                      </Typography>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                        실전 문제 연습으로 완벽 대비 (MPT & MEE)
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <QuestionAnswerIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="실전형 MPT/MEE 문제" 
                          secondary="실제 시험과 동일한 형식의 문제 제공 (2문제/6문제 세트)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PsychologyIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="상세한 해설 및 모범답안 제공" 
                          secondary="문제별 핵심 포인트, 해설, 다양한 우수 답안" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <TimelineIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="성적 분석 및 피드백" 
                          secondary="문제 유형별 성취도, AI/전문가 평가, 맞춤형 피드백" />
                      </ListItem>
                    </List>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
            </Grid>
            {/* Study권 서비스 (NEW) */}
            <Grid item xs={12} md={6} lg={3} sx={{ mt: { xs: 2, md: 4 } }}>
              <Zoom in={isVisible['features-section']} style={{ transitionDelay: '400ms' }}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: theme.palette.grey[50],
                    color: theme.palette.text.primary,
                    position: 'relative',
                    transform: 'translateY(0)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 6,
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: theme.palette.success.main,
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    NEW
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h5" gutterBottom fontWeight={700} color="success.dark">
                        Study 권
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                        다양한 응시자 답안 비교 분석, 전략 수립, AI/타 응시자/Sample 답안 비교, 시간 내 답안 제출, 오답노트/학습시간/나만의 정의 관리
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CompareArrowsIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="다른 응시자 답안 비교 분석" 
                          secondary="우수/평균/다양한 답안 유형별 분석 및 통계" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <MenuBookIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="자신만의 답안 작성 전략 수립" 
                          secondary="강점/약점 파악 및 실전 적용" />
                      </ListItem>
                    </List>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        color="success" 
                        size="small"
                        onClick={handlePricingClick}
                      >
                        MEE 20🐾(냥) 구매
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="success" 
                        size="small"
                        onClick={handlePricingClick}
                      >
                        MPT 30🐾(냥) 구매
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
        </Container>
      </Box>

      {/* 신규 서비스 홍보 박스 */}
      <Box sx={{ mt: 8, mb: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: theme.palette.primary.light, color: theme.palette.primary.dark }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CompareArrowsIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  다양한 응시자 답안 비교 분석
                </Typography>
              </Box>
              <Typography variant="body1" color="text.primary">
                Study권은 내 답안과 우수/평균/다양한 응시자 답안을 유형별로 비교 분석할 수 있어, 실전에서 통하는 답안 작성 전략을 세울 수 있습니다.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: theme.palette.info.light, color: theme.palette.info.dark }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CompareArrowsIcon color="info" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6" fontWeight={700} color="info.dark">
                  AI/타 응시자/Sample 답안 비교 분석
                </Typography>
              </Box>
              <Typography variant="body1" color="text.primary">
                내 답안과 AI, 우수/평균/다양한 응시자, 샘플 답안을 한눈에 비교하고, 실전에서 통하는 답안 작성 전략을 세울 수 있습니다.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: theme.palette.success.light, color: theme.palette.success.dark }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LightbulbIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  총 학습시간/오답노트/나만의 정의 관리
                </Typography>
              </Box>
              <Typography variant="body1" color="text.primary">
                학습 시간 자동 집계, 오답노트, 나만의 정의 추가 등 체계적인 자기주도 학습을 지원합니다.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* 서비스 비교 테이블 */}
      <Box sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            서비스별 비교
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
              <thead>
                <tr style={{ background: theme.palette.primary.light }}>
                  <th style={{ padding: 12, fontWeight: 700, color: theme.palette.primary.dark }}>평가 유형</th>
                  <th style={{ padding: 12, fontWeight: 700, color: theme.palette.primary.dark }}>응답 시간</th>
                  <th style={{ padding: 12, fontWeight: 700, color: theme.palette.primary.dark }}>피드백 상세도</th>
                  <th style={{ padding: 12, fontWeight: 700, color: theme.palette.primary.dark }}>가격</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: theme.palette.background.default }}>
                  <td style={{ padding: 12 }}>AI 평가</td>
                  <td style={{ padding: 12 }}>48시간 이내</td>
                  <td style={{ padding: 12 }}>높음</td>
                  <td style={{ padding: 12 }}>100~150🐾(냥)</td>
                </tr>
                <tr>
                  <td style={{ padding: 12 }}>Professor 평가</td>
                  <td style={{ padding: 12 }}>72시간 이내</td>
                  <td style={{ padding: 12 }}>매우 높음</td>
                  <td style={{ padding: 12 }}>150~200🐾(냥)</td>
                </tr>
                <tr style={{ background: theme.palette.background.default }}>
                  <td style={{ padding: 12 }}>셀프 스터디 (MEE/MPT)</td>
                  <td style={{ padding: 12 }}>즉시</td>
                  <td style={{ padding: 12 }}>다양한 응시자 답안 비교 분석</td>
                  <td style={{ padding: 12 }}>MEE 20🐾(냥) / MPT 30🐾(냥)</td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Paper>
      </Box>

      {/* 합격 스토리 Section - Carousel/Grid Hybrid */}
      <Box
        id="success-section"
        className="animate-section"
        sx={{
          bgcolor: 'background.paper',
          py: 8,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              opacity: isVisible['success-section'] ? 1 : 0,
              transform: isVisible['success-section'] ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.6s ease-out'
            }}
          >
            합격 스토리
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" paragraph>
          커뮤니티 합격 스토리에서 내용을 확인하세요
          </Typography>
          <Grid container spacing={4}>
            {loadingStories ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : successStories.length === 0 ? (
              <Grid item xs={12}>
                <Typography align="center" color="text.secondary">
                  아직 등록된 합격 스토리가 없습니다.
                </Typography>
              </Grid>
            ) : (
              successStories.map((story, index) => (
                <Grid item xs={12} md={6} key={story.id || index}>
                <Slide direction={index % 2 === 0 ? "right" : "left"} in={isVisible['success-section']} timeout={800 + index * 200}>
                  <Card
                    onClick={() => navigate('/community/success-stories')}
                    sx={{
                      height: '100%',
                      background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                      borderRadius: 2,
                      boxShadow: 3,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: theme.palette.primary.main,
                            fontSize: '2rem',
                            fontWeight: 700
                          }}
                        >
                            {story.author?.profiles?.username?.charAt(0) || 'A'}
                        </Avatar>
                        <Box sx={{ ml: 3 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                              {story.title || '제목 없음'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {story.author?.profiles?.username || '익명'}
                          </Typography>
                          <Typography color="text.secondary">
                              {story.state} Bar ({story.exam_date?.slice(0, 4) || ''})
                          </Typography>
                        </Box>
                      </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                            label={`시험일: ${story.exam_date ? story.exam_date.replace(/-/g, '.') : '-'}`}
                            size="small"
                        />
                        <Chip
                            label={`점수: ${story.exam_score ?? '-'}`}
                            size="small"
                            color="primary"
                        />
                        <Chip
                            label={`공부기간: ${story.study_period ?? '-'}`}
                            size="small"
                        />
                      </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 2
                          }}
                        >
                          {story.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {story.created_at ? new Date(story.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                        </Typography>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
              ))
            )}
          </Grid>
        </Container>
      </Box>

      {/* Demo Quiz Section */}
      <Container sx={{ py: 8 }} maxWidth="md">
        <Typography variant="h3" align="center" gutterBottom>
          데모 퀴즈 체험하기
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          실제 Bar Exam 학습 경험을 미리 체험해보세요
        </Typography>
        <DemoQuiz />
      </Container>

      {/* Bar Exam News Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom>
            Bar Exam 소식
          </Typography>
          <Grid container spacing={4}>
            {barExamNews.map((news) => (
              <Grid item key={news.title} xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography gutterBottom variant="h6">
                      {news.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {news.date}
                    </Typography>
                    <Typography variant="body1">
                      {news.content}
                    </Typography>
                    <MuiLink href={news.link} sx={{ mt: 2, display: 'inline-block' }}>
                      자세히 보기
                    </MuiLink>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Community Section - Interactive Grid */}
      <Box
        id="community-section"
        className="animate-section"
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 6,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              opacity: isVisible['community-section'] ? 1 : 0,
              transform: isVisible['community-section'] ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.6s ease-out'
            }}
          >
            커뮤니티
          </Typography>
          <Grid container spacing={3}>
            {communityBoards.map((board, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in={isVisible['community-section']} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    component={RouterLink}
                    to={board.link}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                        '& .hover-content': {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                        '& .board-icon': {
                          transform: 'scale(1.1) translateY(-5px)',
                        }
                      }
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: 2,
                        }}
                      >
                        <Box
                          className="board-icon"
                          sx={{
                            transition: 'transform 0.3s ease',
                            transform: 'scale(1) translateY(0)',
                          }}
                        >
                          {board.icon}
                        </Box>
                        <Typography variant="h6" component="h3" color="text.primary">
                          {board.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {board.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 커뮤니티로 이동 버튼 - 로그인한 사용자만 노출 */}
      {user && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mr: 2 }}>
          <Button variant="contained" color="primary" onClick={() => navigate('/community/general')}>
            커뮤니티로 이동
          </Button>
        </Box>
      )}

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage; 